import React, { useContext, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Form from 'react-bootstrap/Form';
import axios from 'axios';
import { FaArrowLeft, FaChevronDown, FaSearch, FaSignOutAlt, FaTimes } from 'react-icons/fa';
import { Store } from '../../Store';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import { getVisibleSections, normalizeSearch } from './settingsNav';

const NavItemLink = ({ item, t, pathname }) => {
	const isActive = pathname === item.path || pathname.startsWith(item.path + '/');
	const Icon = item.icon;
	return (
		<Link
			to={item.path}
			className={`settings-nav-link ${isActive ? 'active' : ''} ${item.danger ? 'danger' : ''}`}
		>
			<Icon className='settings-nav-icon' />
			<span>{t(item.labelKey)}</span>
		</Link>
	);
};

const CollapsibleGroup = ({ section, items, forceOpen, t, pathname }) => {
	const [open, setOpen] = useState(true);
	const isOpen = forceOpen || open;
	return (
		<div className='settings-nav-group'>
			<button
				type='button'
				className='settings-nav-group-header'
				onClick={() => setOpen((v) => !v)}
				aria-expanded={isOpen}
				disabled={forceOpen}
			>
				<span>{t(section.labelKey)}</span>
				<FaChevronDown className={`settings-nav-chevron ${isOpen ? '' : 'closed'}`} />
			</button>
			{isOpen && (
				<div className='settings-nav-group-items'>
					{(items || section.items).map((item) => (
						<NavItemLink key={item.path} item={item} t={t} pathname={pathname} />
					))}
				</div>
			)}
		</div>
	);
};

/**
 * Inner nav (search + grouped links). Rendered by the desktop sidebar and
 * reused inside the mobile Offcanvas.
 */
export const SidebarNav = () => {
	const { t } = useLabels();
	const { role } = usePermissions();
	const { pathname } = useLocation();
	const [query, setQuery] = useState('');

	// Gating runs before search so hidden items never appear in results.
	const sections = useMemo(() => getVisibleSections(role), [role]);

	const searching = query.trim().length > 0;
	// Groups stay visible with their headers while searching — only items are
	// filtered and only zero-match groups are dropped (matches the "search
	// within groups" spec, rather than flattening into one ungrouped list).
	const matchingSections = useMemo(() => {
		if (!searching) return sections;
		const q = normalizeSearch(query.trim());
		return sections
			.map((section) => ({
				...section,
				items: section.items.filter((item) => normalizeSearch(t(item.labelKey)).includes(q)),
			}))
			.filter((section) => section.items.length > 0);
	}, [searching, query, sections, t]);

	return (
		<nav className='settings-nav'>
			<div className='settings-nav-search'>
				<FaSearch className='settings-nav-search-icon' />
				<Form.Control
					size='sm'
					type='text'
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={t('settings.searchPlaceholder')}
					aria-label={t('settings.searchPlaceholder')}
				/>
				{searching && (
					<button
						type='button'
						className='settings-nav-search-clear'
						onClick={() => setQuery('')}
						aria-label='Limpiar búsqueda'
					>
						<FaTimes />
					</button>
				)}
			</div>
			{searching && matchingSections.length === 0 ? (
				<p className='settings-nav-empty'>{t('settings.noResults')}</p>
			) : (
				matchingSections.map((section) => (
					<CollapsibleGroup
						key={section.key}
						section={section}
						items={section.items}
						forceOpen={searching}
						t={t}
						pathname={pathname}
					/>
				))
			)}
		</nav>
	);
};

/**
 * Sidebar footer: back-to-app link + user block with signout. The settings
 * area hides the AdminNavbar (separate admin mode), so this is the only
 * chrome carrying identity/exit — rendered by the desktop sidebar and the
 * mobile Offcanvas alike.
 */
export const SidebarFooter = () => {
	const { t } = useLabels();
	const { userInfo } = usePermissions();
	const { dispatch: ctxDispatch } = useContext(Store);
	const navigate = useNavigate();

	// Same signout flow as AdminNavbar: clear local session first, then
	// best-effort server invalidation.
	const signoutHandler = async () => {
		const refreshToken = localStorage.getItem('refreshToken');
		const storedUser = JSON.parse(localStorage.getItem('userInfo'));
		ctxDispatch({ type: 'USER_SIGNOUT' });
		localStorage.removeItem('userInfo');
		localStorage.removeItem('refreshToken');
		if (storedUser && refreshToken) {
			try {
				await axios.post(
					'/api/auth/logout',
					{ refreshToken, userId: storedUser._id },
					{ headers: { Authorization: `Bearer ${storedUser.token}` } },
				);
			} catch (error) {
				console.error('Error invalidating session on server:', error);
			}
		}
		navigate('/signin');
	};

	return (
		<div className='settings-sidebar-footer'>
			<Link to='/AdminScreen' className='settings-nav-link settings-back-link'>
				<FaArrowLeft className='settings-nav-icon' />
				<span>{t('settings.backToApp')}</span>
			</Link>
			{userInfo && (
				<div className='settings-user-block'>
					<div className='settings-user-avatar'>
						{(userInfo.name || '?').charAt(0).toUpperCase()}
					</div>
					<div className='settings-user-identity'>
						<span className='settings-user-name'>{userInfo.name}</span>
						{userInfo.email && <span className='settings-user-email'>{userInfo.email}</span>}
					</div>
				</div>
			)}
			<button type='button' className='settings-nav-link settings-signout' onClick={signoutHandler}>
				<FaSignOutAlt className='settings-nav-icon' />
				<span>{t('settings.signOut')}</span>
			</button>
		</div>
	);
};

export default function SettingsSidebar() {
	const { t } = useLabels();
	return (
		<aside className='settings-sidebar d-none d-lg-flex'>
			<Link to='/AdminScreen/Config' className='settings-sidebar-title'>
				{t('settings.title')}
			</Link>
			<SidebarNav />
			<SidebarFooter />
		</aside>
	);
}
